import { SiteUrls } from '@tableau/shared-urls';

import { Messages } from '../../../gen-src/Localize';
import { isMetricsEnabled } from '../../metrics/util/MetricUtils';
import { getSessionManagementServiceInstance, SessionManagementService } from '../services/SessionManagementService';
import { ExplorableItemType } from '../../spacesPlatform/models/ContentType';

import { ContentSuggestionDataItem, ContentType } from './Types';
import { CollectionUtils } from '../../Collections/utils/CollectionUtils';
import { collectionIcon, databaseIcon, dataRoleIcon, dataSourceIcon, flowIcon, lensIcon, metricIcon, projectIcon, tableIcon, viewIcon, virtualConnectionIcon, workbookIcon } from '../../img/svgIcons';
import { ContentFiltersKeys } from '../../spacesPlatform/models/ContentFilters';

const emptySearch = '?' + ContentFiltersKeys.search + '=';

export type ContentDefinition = {
    getName: () => string;
    icon: string;
    getUrl: (urls: SiteUrls, item: ContentSuggestionDataItem) => string;
    getSearchUrl: (urls: SiteUrls) => string;
    explorableItemType: ExplorableItemType;
    shouldBeAvailable: () => boolean;
};

export const ContentDefinitions: { [type in ContentType]: ContentDefinition } = {
    [ContentType.view]: {
        getName: () => Messages.views(),
        icon: viewIcon,
        getUrl: (urls, item) => urls.viewUrl(item.content.path),
        getSearchUrl: (urls) => urls.searchViewsUrl() + emptySearch,
        explorableItemType: 'view',
        shouldBeAvailable: () => true
    },
    [ContentType.metric]: {
        getName: () => Messages.metrics(),
        icon: metricIcon,
        getUrl: (urls, item) => urls.metricUrl(item.content.id.toString()),
        getSearchUrl: (urls) => urls.searchMetricsUrl() + emptySearch,
        explorableItemType: 'metric',
        shouldBeAvailable: () => isMetricsEnabled()
    },
    [ContentType.lens]: {
        getName: () => Messages.lenses(),
        icon: lensIcon,
        getUrl: (urls, item) => urls.lensUrl(item.content.id.toString()),
        getSearchUrl: (urls) => urls.searchLensesUrl() + emptySearch,
        explorableItemType: 'lens',
        shouldBeAvailable: () => SessionManagementService.instance.isNlpServiceEnabled() &&
            (SessionManagementService.instance.askDataMode() !== 'disabled_always')
    },
    [ContentType.unifieddatasource]: {
        getName: () => Messages.datasources(),
        icon: dataSourceIcon,
        getUrl: (urls, item) => urls.datasourceUrl(item.content.id.toString()),
        getSearchUrl: (urls) => urls.searchDatasourcesUrl() + emptySearch,
        explorableItemType: 'datasource',
        shouldBeAvailable: () => true
    },
    [ContentType.workbook]: {
        getName: () => Messages.workbooks(),
        icon: workbookIcon,
        getUrl: (urls, item) => urls.workbookUrl(item.content.id.toString()),
        getSearchUrl: (urls) => urls.searchWorkbooksUrl() + emptySearch,
        explorableItemType: 'workbook',
        shouldBeAvailable: () => true
    },
    [ContentType.collection]: {
        getName: () => Messages.collections(),
        icon: collectionIcon,
        getUrl: (urls, item) => urls.collectionUrl(item.content.luid.toString()),
        getSearchUrl: (urls) => urls.searchCollectionsUrl() + emptySearch,
        explorableItemType: 'collection',
        shouldBeAvailable: () => CollectionUtils.checkCollectionSiteSetting()
    },
    [ContentType.project]: {
        getName: () => Messages.projects(),
        icon: projectIcon,
        getUrl: (urls, item) => urls.projectUrl(item.content.id.toString()),
        getSearchUrl: (urls) => urls.searchProjectsUrl() + emptySearch,
        explorableItemType: 'project',
        shouldBeAvailable: () => true
    },
    [ContentType.flow]: {
        getName: () => Messages.flows(),
        icon: flowIcon,
        getUrl: (urls, item) => urls.flowUrl(item.content.id.toString()),
        getSearchUrl: (urls) => urls.searchFlowsUrl() + emptySearch,
        explorableItemType: 'flow',
        shouldBeAvailable: () => true
    },
    [ContentType.datarole]: {
        getName: () => Messages.dataRoles(),
        icon: dataRoleIcon,
        getUrl: (urls, item) => urls.dataRoleUrl(item.content.id.toString()),
        getSearchUrl: (urls) => urls.searchDataRolesUrl() + emptySearch,
        explorableItemType: 'dataRole',
        shouldBeAvailable: () => true
    },
    [ContentType.table]: {
        getName: () => Messages.tables(),
        icon: tableIcon,
        getUrl: (urls, item) => urls.catalogTableUrl(item.content.graphQLId?.toString()),
        getSearchUrl: (urls) => urls.searchTablesUrl() + emptySearch,
        explorableItemType: 'tableWithCrass',
        shouldBeAvailable: () => {
            const sessionManagementService: SessionManagementService = getSessionManagementServiceInstance();
            return sessionManagementService.catalogingOnSiteEnabled();
        }
    },
    [ContentType.database]: {
        getName: () => Messages.databasesAndFiles(),
        icon: databaseIcon,
        getUrl: (urls, item) => urls.catalogDatabaseUrl(item.content.graphQLId?.toString()),
        getSearchUrl: (urls) => urls.searchDatabasesUrl() + emptySearch,
        explorableItemType: 'database',
        shouldBeAvailable: () => {
            const sessionManagementService: SessionManagementService = getSessionManagementServiceInstance();
            return sessionManagementService.catalogingOnSiteEnabled();
        }
    },
    [ContentType.virtualconnection]: {
        getName: () => Messages.publishedConnections(),
        icon: virtualConnectionIcon,
        getUrl: (urls, item) => urls.virtualConnectionUrl(item.content.id.toString()),
        getSearchUrl: (urls) => urls.searchVirtualConnectionsUrl() + emptySearch,
        explorableItemType: 'virtualConnection',
        shouldBeAvailable: () => true
    },
};
